import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '~/components/Dialog'
import notify from '~/components/Notify'
import SimpleFileUpload from '~/components/SimpleFileUpload'
import TwoFaComp from '~/components/TwoFaComp'
import { useAuth } from '~/routes/components/AuthProvider'
import { KeyLabelType } from '~/types'
import ENV from '~/environment';
import { handleErrors } from '~/utils/helpers'
import TableComponent from '~/components/TableComponent'

interface Props {
  isArticlesImportOpen: boolean
  selectedMonth: KeyLabelType
  handleClose: () => void
}

const ArticlesImportModal: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { systemData: { roleSettings: { tfaArticlesImport }, logged: { tfaType } } } = useAuth()
  const { isArticlesImportOpen, handleClose, selectedMonth } = props

  const [articlesFile, setArticlesFile] = useState(null)
  const [isBusy, setIsBusy] = useState(false)
  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const [tfaCode, setTfaCode] = useState('')
  const [cancelToken, setCancelToken] = useState(axios.CancelToken.source());
  const [articles, setArticles] = useState(null)

  const tableColumnHeaders = [
    {
      name: t('Supplier No.'),
      key: 'supplierNo'
    },
    {
      name: t('Company name'),
      key: 'farmerName'
    },
    {
      name: t('Article No.'),
      key: 'articleNumber'
    },
    {
      name: t('Article name'),
      key: 'articleName'
    },
    {
      name: t('Amount'),
      key: 'articleAmount'
    },
    {
      name: t('Status'),
      key: 'status',
      type: 'verified'
    }
  ]

  useEffect(() => {
    //cleanup
    return () => {
      setCancelToken(null)
    }
  }, [])

  async function handleLoadArticles(file: File) {
    const formData = new FormData();
    formData.append('month', selectedMonth.key)
    formData.append('file', file)
    const url = '/milk-money/milk-payment/articles/import/get-data'
    setIsBusy(true)
    try {
      const response = await axios.post(
        `${ENV.API_URL}${url}`,
        formData,
        {
          headers: { 'Authorization': localStorage.getItem('token') },
          cancelToken: cancelToken.token
        });

      setArticles(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderFileImport() {
    return <div>
      <SimpleFileUpload
        value={articlesFile}
        onChange={(file: File) => {
          setArticlesFile(file)
          handleLoadArticles(file)
        }}
        sizeLimitInMB={300}
        accept={['.xlsx']} />
    </div>
  }

  function handleCloseModal() {
    setArticlesFile(null)
    setArticles(null)
    handleClose()
  }

  function renderArticles() {
    return <TableComponent
      tableColumnHeaders={tableColumnHeaders}
      rows={articles?.rows || []}
      total={articles?.total || 0}
      removePagination
    />
  }

  async function handleSubmitArticles() {
    setIsBusy(true)
    try {
      const formData = new FormData();
      formData.append('month', selectedMonth.key)
      formData.append('file', articlesFile)
      if (tfaArticlesImport) {
        formData.append('code', tfaCode)
      }
      const url = '/milk-money/milk-payment/articles/import'
      await axios.post(
        `${ENV.API_URL}${url}`,
        formData,
        {
          headers: { 'Authorization': localStorage.getItem('token') },
          cancelToken: cancelToken.token
        });
      notify(t('Successfully imported article bookings'), 'success')
      handleCloseModal()
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  return <Dialog
    isDialogOpen={isArticlesImportOpen}
    noScrollingContent={false}
    isLoading={isBusy}
    textHeader={t('Import Article Bookings ({{selectedMonth}})', { selectedMonth: selectedMonth?.label })}
    textClose={t('Cancel')}
    disableSubmit={tfaArticlesImport ? !articlesFile || !correctTfaCode : !articlesFile}
    handleClose={() => handleCloseModal()}
    handleSubmit={() => handleSubmitArticles()}
    textSubmit={t('Import')}
    actionContent={articles && <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginRight: tfaArticlesImport ? 0 : 200 }}>
      <div style={{ fontWeight: 'bold', color: 'green' }}>{t('Success: {{total}}', { total: articles?.success })}</div>
      <div style={{ fontWeight: 'bold', color: 'red' }}>{t('Fail: {{fail}}', { fail: articles?.error })}</div>
      {Boolean(tfaArticlesImport) && articles && !isBusy && <TwoFaComp
        onChangeTfaCode={(code) => setTfaCode(code)}
        disableMarginTop
        onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
      />}
    </div>}
    textContent={<div>
      {renderFileImport()}
      <div style={{ marginTop: '1em' }}>
        {articles && !isBusy && renderArticles()}
      </div>
    </div>}
  />
}
export default ArticlesImportModal